import React, { useCallback, useEffect, useState } from 'react'
import { ApiFetch, ApiPut } from './components/Common/Api'
import { IdpFetch } from './components/Common/IdentityPortal'
import Cookies from 'js-cookie'
import type { County } from './models/User'

export type User = {
    roles: string[]
    token?: string
    specialty?: string
    providerCounties?: County[]
    providerName?: string
    name?: string
    username?: string
    userID?: number
    associatedIDs?: number[]
    bidTourViewed?: boolean
    serviceTourViewed?: boolean
}

type CacheEntry = {
    data: unknown
    expires: Date
}
type Cache = {
    [index: string]: CacheEntry
}

type UserContextState = {
    user: User
    setUser: React.Dispatch<User>
    roles: string[]
    token?: string
    cache: Cache
    cachedGet: <T extends unknown>(uniqueKey: string, expireMinutes: number, getter: () => Promise<T>) => Promise<T>
    cacheDelete: (uniqueKey: string) => void
    authenticateUser: (token?: string, callback?: (success: boolean) => void) => void
    hasAnyRole: (...roles: string[]) => boolean
    logoutUser: () => void
    hasSeenCommPrefModal: boolean | null
    markCommPrefModalAsSeen: () => Promise<boolean>
}

let refreshTokenTimer: number

export const UserContext = React.createContext<UserContextState>({} as UserContextState)

export const UserProvider: React.FC = ({ children }) => {
    const [user, setUser] = useState<User>({ roles: [] })
    const [hasSeenCommPrefModal, setHasSeenCommPrefModal] = useState<boolean | null>(null);

    const refreshToken = useCallback(() => {
        IdpFetch<User>('refresh', undefined, true).then(
            (result) => {
                if (Cookies.get('cs_refresh_token_time')) {
                    clearTimeout(refreshTokenTimer)
                    refreshTokenTimer = window.setTimeout(refreshToken, new Date(parseInt(Cookies.get('cs_refresh_token_time') as string)).getTime() - new Date().getTime())
                }
            }
        )
            // eslint-disable-next-line no-console
            .catch(console.log)
    }, [])

    const authenticateUser = useCallback((token?: string, callback?: (success: boolean) => void) => {
        // eslint-disable-next-line no-console
        console.log('Fetching User')
        ApiFetch<User>('SSO').then(
            (result) => {
                setUser({ ...result, token })

                if (Cookies.get('cs_refresh_token_time')) {
                    clearTimeout(refreshTokenTimer)
                    refreshTokenTimer = window.setTimeout(refreshToken, new Date(parseInt(Cookies.get('cs_refresh_token_time') as string)).getTime() - new Date().getTime())
                }

                if (callback)
                    callback(true)
                
                // eslint-disable-next-line no-console
                // navigate('/').catch(console.log)
            },
            (e) => {
                // eslint-disable-next-line no-console
                console.error('Error Fetching User', e)
                
                if (callback)
                    callback(false)
                else
                    window.location.href = '/api/Auth/Logout'
            }
        )
            // eslint-disable-next-line no-console
            .catch(console.log)
    }, [refreshToken])

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('Is User authed', !!user.token, !!Cookies.get('cs_id_token'), Cookies.get('cs_id_token') ? `${Cookies.get('cs_id_token')?.substring(0, 10) as string} ...` : 'no token')
        if (window.location.pathname.toLowerCase() !== '/logout' && !user.token && !!Cookies.get('cs_id_token')) authenticateUser(Cookies.get('cs_id_token'))
    }, [authenticateUser, user])

    const logoutUser = useCallback(() => {
        setUser({ roles: [] })
        window.location.href = '/api/Auth/Logout'
    }, [])

    const hasAnyRole = useCallback((...roles) => roles.some(r => user.roles.includes(r)), [user])

    const cache: Cache = {}

    const cachedGet = async <T extends unknown> (uniqueKey: string, expireMinutes: number, getter: () => Promise<T>) => {
        if (!cache[uniqueKey] || new Date(cache[uniqueKey].expires) < new Date()) {
            const data = await getter()
            if (data) {
                const now = new Date()
                now.setTime(now.getTime() + (expireMinutes * 60000))
                cache[uniqueKey] = {
                    expires: new Date(now),
                    data: data
                }
            }
        }
        return cache[uniqueKey].data as T
    }

    const cacheDelete = (uniqueKey: string) => {
        delete cache[uniqueKey]
    } 

    useEffect(() => {
        ApiFetch<boolean>('User/HasSeenCommPrefModal')
            .then(
                (result) => {
                    setHasSeenCommPrefModal(result);
                },
                (error: string) => {
                    setHasSeenCommPrefModal(null); 
                }
            )
    }, []);

    const markCommPrefModalAsSeen = () => {
        setHasSeenCommPrefModal(true);
        return ApiPut<boolean>('User/HasSeenCommPrefModal', undefined, { hasSeenCommPrefModal: true })
            .then((result) => {
                return result;
            })
            .catch((error) => {
                setHasSeenCommPrefModal(null);
                return false;
            });
    };

    return <UserContext.Provider
        children={children}
        value={{
            user: user,
            roles: user.roles,
            token: user.token,
            cache: cache,
            cachedGet,
            cacheDelete,
            authenticateUser,
            hasAnyRole,
            logoutUser,
            setUser,
            hasSeenCommPrefModal,
            markCommPrefModalAsSeen
        }} />
}

export const Roles = {
    WaiverServiceCoordinator: 'Waiver Service Coordinator',
    WaiverServiceLeader: 'Waiver Service Leader',
    MarketAdmin: 'Market Admin',
    OversightApprover: 'Oversight Approver',
    ReadOnly: 'Read Only',
    ITSupport: 'IT Support',
    Provider: 'Provider',
    Impersonator: 'Impersonator'
}

export default UserContext
export type { UserContextState }